import { useState, useMemo, useCallback, useRef } from "react";
import { formatJamaicaDate as formatDate, formatJamaicaDateTime as formatDateTime } from "@/lib/datetime";
import { LatexText } from "@/components/LatexText";
import { QuestionPreviewDialog } from "@/components/QuestionPreviewDialog";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  Trash2,
  Pencil,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  X,
  ImageIcon,
  Eye,
  CheckCircle2,
  XCircle,
  History,
  Plus,
  ArrowRight,
  Check,
  RotateCcw,
  ArrowUpDown,
  Flag,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface QuestionObjective {
  code: string;
  description: string;
  topic: string;
}

interface QuizzativeQuestion {
  id: string;
  grade: number;
  subject: string;
  topic: string;
  questionType: string;
  questionText: string;
  questionImageUrl: string | null;
  options: string;
  correctAnswer: string;
  explanation: string | null;
  difficulty: string;
  status: string;
  isActive: boolean;
  quizzativeId: string;
  quizzativeQuizId: string;
  quizzativeData: any;
  createdAt: string;
  updatedAt: string;
  objectives?: QuestionObjective[];
}

interface FilterOption {
  value: string;
  label: string;
  count: number;
}

interface QuestionsResponse {
  data: QuizzativeQuestion[];
  meta: {
    currentPage: number;
    perPage: number;
    total: number;
    lastPage: number;
  };
  filters: {
    grades: { value: string; label: string }[];
    subjects: FilterOption[];
    topics: FilterOption[];
  };
}

interface CurriculumGrade {
  id: string;
  gradeNumber: number;
  displayName: string;
  isActive: boolean;
}

interface CurriculumSubject {
  id: string;
  gradeId: string;
  name: string;
  displayName: string;
  isActive: boolean;
}

interface CurriculumTopic {
  id: string;
  subjectId: string;
  name: string;
  displayName: string;
  isActive: boolean;
  subject: CurriculumSubject;
}

const FIELD_LABELS: Record<string, string> = {
  questionText: "Question Text",
  questionImageUrl: "Image",
  options: "Answer Choices",
  correctAnswer: "Correct Answer",
  explanation: "Explanation",
  difficulty: "Difficulty",
  grade: "Grade",
  subject: "Subject",
  topic: "Topic",
  hint: "Hint",
};

function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>(undefined);
  const prev = ref.current;
  ref.current = value;
  return prev;
}

function getOriginalData(q: QuizzativeQuestion) {
  if (!q.quizzativeData) return null;
  try {
    const raw = typeof q.quizzativeData === "string" ? JSON.parse(q.quizzativeData) : q.quizzativeData;
    if (typeof raw === "string") {
      return JSON.parse(raw);
    }
    return raw;
  } catch {
    return null;
  }
}

function QuestionChangeHistory({ questionId }: { questionId: string }) {
  const { data: changes, isLoading } = useQuery<Array<{
    id: string;
    changeType: string;
    changedFields: Record<string, { old: any; new: any }> | null;
    createdAt: string;
  }>>({
    queryKey: [`/api/admin/quizzative/questions/${questionId}/changes`],
    enabled: !!questionId,
  });

  if (isLoading) return <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Loading history...</div>;
  if (!changes || changes.length === 0) return <div className="text-xs text-muted-foreground">No change history recorded yet.</div>;

  return (
    <div className="space-y-3">
      {changes.map((change) => (
        <div key={change.id} className="border rounded-md p-3">
          <div className="flex items-center gap-2 mb-2">
            {change.changeType === "created" ? (
              <Plus className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
            ) : (
              <Pencil className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
            )}
            <span className="text-xs font-medium capitalize">{change.changeType}</span>
            <span className="text-xs text-muted-foreground">
              {formatDateTime(change.createdAt)}
            </span>
          </div>
          {change.changeType === "updated" && change.changedFields && (
            <div className="space-y-1.5 ml-5">
              {Object.entries(change.changedFields).map(([field, { old: oldVal, new: newVal }]) => {
                const label = FIELD_LABELS[field] || field;
                const oldDisplay = field === "options" ? "(choices updated)" : String(oldVal ?? "(empty)");
                const newDisplay = field === "options" ? "(choices updated)" : String(newVal ?? "(empty)");
                return (
                  <div key={field} className="text-xs">
                    <span className="font-medium">{label}:</span>{" "}
                    <span className="text-red-600 dark:text-red-400 line-through">{oldDisplay.length > 80 ? oldDisplay.substring(0, 80) + "..." : oldDisplay}</span>
                    <ArrowRight className="h-3 w-3 inline mx-1 text-muted-foreground" />
                    <span className="text-green-600 dark:text-green-400">{newDisplay.length > 80 ? newDisplay.substring(0, 80) + "..." : newDisplay}</span>
                  </div>
                );
              })}
            </div>
          )}
          {change.changeType === "created" && (
            <div className="text-xs text-muted-foreground ml-5">Question first synced from Quizzative</div>
          )}
        </div>
      ))}
    </div>
  );
}

function InlinePlacementEditor({
  question,
  grades,
  systemSubjects,
  systemTopics,
  systemGrades,
}: {
  question: QuizzativeQuestion;
  grades: CurriculumGrade[];
  systemSubjects: CurriculumSubject[];
  systemTopics: CurriculumTopic[];
  systemGrades: CurriculumGrade[];
}) {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [editGrade, setEditGrade] = useState(question.grade.toString());
  const [editSubject, setEditSubject] = useState(question.subject);
  const [editTopic, setEditTopic] = useState(question.topic);

  const syncStateFromProps = () => {
    setEditGrade(question.grade.toString());
    setEditSubject(question.subject);
    setEditTopic(question.topic);
  };

  const prevQuestion = usePrevious(question);
  if (prevQuestion && (
    prevQuestion.grade !== question.grade ||
    prevQuestion.subject !== question.subject ||
    prevQuestion.topic !== question.topic
  )) {
    if (!editing) {
      syncStateFromProps();
    }
  }

  const original = getOriginalData(question);

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, any>) => {
      const res = await apiRequest("PATCH", `/api/admin/quizzative/questions/${question.id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/quizzative/questions"] });
      setEditing(false);
      toast({ title: "Placement updated" });
    },
    onError: (error: Error) => {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
    },
  });

  const getSubjectsForGrade = (gradeNum: string) => {
    if (!gradeNum || !systemGrades.length || !systemSubjects.length) return [];
    const grade = systemGrades.find(g => g.gradeNumber.toString() === gradeNum);
    if (!grade) return [];
    return systemSubjects.filter(s => s.gradeId === grade.id && s.isActive);
  };

  const getTopicsForGradeAndSubject = (gradeNum: string, subjectName: string) => {
    if (!systemTopics.length || !systemSubjects.length || !systemGrades.length) return [];
    let filtered = [...systemTopics];

    if (gradeNum) {
      const grade = systemGrades.find(g => g.gradeNumber.toString() === gradeNum);
      if (grade) {
        const gradeSubjectIds = new Set(
          systemSubjects.filter(s => s.gradeId === grade.id).map(s => s.id)
        );
        filtered = filtered.filter(t => gradeSubjectIds.has(t.subjectId));
      }
    }

    if (subjectName) {
      const matchingSubjectIds = new Set(
        systemSubjects.filter(s => s.name.toLowerCase() === subjectName.toLowerCase()).map(s => s.id)
      );
      filtered = filtered.filter(t => matchingSubjectIds.has(t.subjectId));
    }

    const seen = new Map<string, CurriculumTopic>();
    for (const t of filtered) {
      if (!seen.has(t.name.toLowerCase())) {
        seen.set(t.name.toLowerCase(), t);
      }
    }
    return Array.from(seen.values());
  };

  const getSubjectLabel = useCallback((s: string) => {
    const found = systemSubjects.find(x => x.name.toLowerCase() === s.toLowerCase());
    return found?.displayName || s;
  }, [systemSubjects]);

  const hasChanges = editGrade !== question.grade.toString() || editSubject !== question.subject || editTopic !== question.topic;

  const handleSave = () => {
    updateMutation.mutate({
      grade: parseInt(editGrade),
      subject: editSubject,
      topic: editTopic,
    });
  };

  const handleCancel = () => {
    setEditGrade(question.grade.toString());
    setEditSubject(question.subject);
    setEditTopic(question.topic);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge variant="secondary">G{question.grade}</Badge>
          <Badge variant="secondary" className="capitalize">{getSubjectLabel(question.subject)}</Badge>
          <Badge variant="outline">{question.topic}</Badge>
          <Button variant="ghost" size="icon" onClick={() => { syncStateFromProps(); setEditing(true); }} className="h-6 w-6">
            <Pencil className="h-3 w-3" />
          </Button>
        </div>
        {question.objectives && question.objectives.length > 0 ? (
          <div className="flex items-center gap-1 flex-wrap mt-0.5">
            {question.objectives.slice(0, 2).map((obj, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-[10px] bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 px-1.5 py-0.5 rounded font-medium" title={obj.description}>
                {obj.code || obj.topic}
              </span>
            ))}
          </div>
        ) : (
          <span className="inline-flex items-center text-[10px] bg-orange-500/15 text-orange-600 dark:text-orange-400 px-1.5 py-0.5 rounded font-medium mt-0.5">
            Untagged
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-1.5 items-end flex-wrap">
        <div className="w-24">
          <Select value={editGrade} onValueChange={(v) => { setEditGrade(v); setEditTopic(""); }}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {grades.map(g => (
                <SelectItem key={g.gradeNumber} value={g.gradeNumber.toString()}>{g.displayName}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-28">
          <Select value={editSubject} onValueChange={(v) => { setEditSubject(v); setEditTopic(""); }}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {getSubjectsForGrade(editGrade).map(s => (
                <SelectItem key={s.id} value={s.name.toLowerCase()}>{s.displayName}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-36">
          <Select value={editTopic} onValueChange={setEditTopic}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Topic" />
            </SelectTrigger>
            <SelectContent>
              {getTopicsForGradeAndSubject(editGrade, editSubject).map(t => (
                <SelectItem key={t.id} value={t.name}>{t.displayName}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex gap-1">
        <Button
          variant="default"
          size="sm"
          className="h-7 text-xs"
          disabled={!hasChanges || updateMutation.isPending}
          onClick={handleSave}
        >
          {updateMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Check className="h-3 w-3 mr-1" />}
          Save
        </Button>
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleCancel}>
          <RotateCcw className="h-3 w-3 mr-1" />
          Cancel
        </Button>
      </div>
    </div>
  );
}

const REASON_LABELS: Record<string, string> = {
  incorrect_answer: "Incorrect Answer",
  unclear_question: "Unclear Question",
  wrong_topic: "Wrong Topic",
  offensive_content: "Offensive Content",
  duplicate: "Duplicate Question",
  other: "Other",
};

const STATUS_STYLES: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  reviewed: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  resolved: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  dismissed: "bg-muted text-muted-foreground",
};

interface ReportItem {
  id: string;
  questionId: string;
  userId: string;
  reason: string;
  details: string | null;
  status: string;
  adminNotes: string | null;
  createdAt: string;
  resolvedAt: string | null;
  questionText: string;
  reporterName: string;
}

function FlaggedQuestions() {
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [reportPage, setReportPage] = useState(1);
  const [updatingReport, setUpdatingReport] = useState<ReportItem | null>(null);
  const [updateStatus, setUpdateStatus] = useState("");
  const [updateNotes, setUpdateNotes] = useState("");

  const { data: counts } = useQuery<{ pending: number; reviewed: number; resolved: number; dismissed: number; total: number }>({
    queryKey: ['/api/admin/question-reports/counts'],
  });

  const queryParams = new URLSearchParams();
  queryParams.set("page", reportPage.toString());
  queryParams.set("limit", "20");
  if (statusFilter) queryParams.set("status", statusFilter);

  const { data: reportsData, isLoading: reportsLoading } = useQuery<{ reports: ReportItem[]; total: number; page: number; limit: number }>({
    queryKey: ['/api/admin/question-reports', reportPage, statusFilter],
    queryFn: async () => {
      const res = await fetch(`/api/admin/question-reports?${queryParams.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
  });

  const updateReportMutation = useMutation({
    mutationFn: async ({ id, status, adminNotes }: { id: string; status: string; adminNotes?: string }) => {
      const res = await apiRequest("PATCH", `/api/admin/question-reports/${id}`, { status, adminNotes });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/question-reports'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/question-reports/counts'] });
      setUpdatingReport(null);
      setUpdateStatus("");
      setUpdateNotes("");
      toast({ title: "Report updated" });
    },
    onError: (error: Error) => {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
    },
  });

  const reports = reportsData?.reports || [];
  const totalReports = reportsData?.total || 0;
  const totalPages = Math.ceil(totalReports / 20);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        {counts && (
          <>
            <Badge className={STATUS_STYLES.pending}>
              Pending: {counts.pending}
            </Badge>
            <Badge className={STATUS_STYLES.reviewed}>
              Reviewed: {counts.reviewed}
            </Badge>
            <Badge className={STATUS_STYLES.resolved}>
              Resolved: {counts.resolved}
            </Badge>
            <Badge className={STATUS_STYLES.dismissed}>
              Dismissed: {counts.dismissed}
            </Badge>
          </>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setReportPage(1); }}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="dismissed">Dismissed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {reportsLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : reports.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No flagged questions found.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {reports.map((report) => (
            <Card key={report.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0 space-y-2">
                    <p className="text-sm font-medium leading-snug line-clamp-2">
                      {report.questionText.length > 150 ? report.questionText.substring(0, 150) + "..." : report.questionText}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={STATUS_STYLES[report.status] || ""} variant="secondary">
                        {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                      </Badge>
                      <Badge variant="outline">
                        {REASON_LABELS[report.reason] || report.reason}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        by {report.reporterName}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(report.createdAt)}
                      </span>
                    </div>
                    {report.details && (
                      <p className="text-xs text-muted-foreground italic">
                        "{report.details.length > 100 ? report.details.substring(0, 100) + "..." : report.details}"
                      </p>
                    )}
                    {report.adminNotes && (
                      <p className="text-xs text-muted-foreground">
                        Admin notes: {report.adminNotes}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setUpdatingReport(report);
                      setUpdateStatus(report.status);
                      setUpdateNotes(report.adminNotes || "");
                    }}
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    Review
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Page {reportPage} of {totalPages} ({totalReports} total)
              </span>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={reportPage <= 1} onClick={() => setReportPage(reportPage - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={reportPage >= totalPages} onClick={() => setReportPage(reportPage + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog open={!!updatingReport} onOpenChange={(open) => { if (!open) setUpdatingReport(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Report</DialogTitle>
            <DialogDescription>
              Update the status of this reported question.
            </DialogDescription>
          </DialogHeader>
          {updatingReport && (
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs text-muted-foreground">Question</Label>
                <p className="text-sm mt-1">{updatingReport.questionText}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline">{REASON_LABELS[updatingReport.reason] || updatingReport.reason}</Badge>
                <span className="text-xs text-muted-foreground">Reported by {updatingReport.reporterName}</span>
              </div>
              {updatingReport.details && (
                <div>
                  <Label className="text-xs text-muted-foreground">Reporter Details</Label>
                  <p className="text-sm mt-1 italic">"{updatingReport.details}"</p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={updateStatus} onValueChange={setUpdateStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="reviewed">Reviewed</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="dismissed">Dismissed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Admin Notes (optional)</Label>
                <Textarea
                  value={updateNotes}
                  onChange={(e) => setUpdateNotes(e.target.value)}
                  placeholder="Add notes about this report..."
                  className="resize-none"
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdatingReport(null)}>Cancel</Button>
            <Button
              disabled={updateReportMutation.isPending}
              onClick={() => {
                if (updatingReport) {
                  updateReportMutation.mutate({
                    id: updatingReport.id,
                    status: updateStatus,
                    adminNotes: updateNotes || undefined,
                  });
                }
              }}
            >
              {updateReportMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Update Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function QuizzativeQuestions() {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [gradeFilter, setGradeFilter] = useState<string>("");
  const [subjectFilter, setSubjectFilter] = useState<string>("");
  const [topicFilter, setTopicFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [viewingQuestion, setViewingQuestion] = useState<QuizzativeQuestion | null>(null);
  const [previewQuestion, setPreviewQuestion] = useState<QuizzativeQuestion | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<QuizzativeQuestion | null>(null);
  const [editForm, setEditForm] = useState<{questionText: string; options: string; correctAnswer: string; explanation: string; difficulty: string}>({questionText: "", options: "", correctAnswer: "", explanation: "", difficulty: ""});
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkGrade, setBulkGrade] = useState("");
  const [bulkSubject, setBulkSubject] = useState("");
  const [bulkTopic, setBulkTopic] = useState("");
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const purgeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", "/api/admin/quizzative/questions/purge-all");
      return res.json();
    },
    onSuccess: (data) => {
      toast({ title: "Purge Complete", description: `Deleted ${data.deleted} Quizzative questions. Sync history cleared.` });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/quizzative/questions"] });
      setShowPurgeConfirm(false);
    },
    onError: (error: any) => {
      toast({ title: "Purge Failed", description: error.message, variant: "destructive" });
    },
  });

  const editQuestionMutation = useMutation({
    mutationFn: async (data: { id: string; updates: Record<string, any> }) => {
      const res = await apiRequest("PUT", `/api/admin/questions/${data.id}`, data.updates);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Question Updated", description: "Changes saved successfully." });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/quizzative/questions"] });
      setEditingQuestion(null);
      setViewingQuestion(null);
    },
    onError: (error: any) => {
      toast({ title: "Update Failed", description: error.message, variant: "destructive" });
    },
  });

  function startEditing(q: QuizzativeQuestion) {
    setEditingQuestion(q);
    let optionsStr = "";
    try {
      const parsed = typeof q.options === "string" ? JSON.parse(q.options) : q.options;
      const arr = typeof parsed === "string" ? JSON.parse(parsed) : parsed;
      if (Array.isArray(arr)) {
        optionsStr = arr.map((o: any) => o.text).join("\n");
      }
    } catch {}
    setEditForm({
      questionText: q.questionText || "",
      options: optionsStr,
      correctAnswer: q.correctAnswer || "",
      explanation: q.explanation || "",
      difficulty: q.difficulty || "medium",
    });
  }

  function saveEdit() {
    if (!editingQuestion) return;
    const optionLines = editForm.options.split("\n").filter(l => l.trim());
    const optionsArr = optionLines.map((text, i) => ({ id: String.fromCharCode(97 + i), text: text.trim() }));
    editQuestionMutation.mutate({
      id: editingQuestion.id,
      updates: {
        questionText: editForm.questionText,
        options: optionsArr,
        correctAnswer: editForm.correctAnswer,
        explanation: editForm.explanation,
        difficulty: editForm.difficulty,
      },
    });
  }

  const { data: systemGrades } = useQuery<CurriculumGrade[]>({
    queryKey: ["/api/curriculum/grades"],
  });

  const { data: systemSubjects } = useQuery<CurriculumSubject[]>({
    queryKey: ["/api/curriculum/subjects"],
  });

  const { data: systemTopics } = useQuery<CurriculumTopic[]>({
    queryKey: ["/api/curriculum/topics"],
  });

  const grades = useMemo(() =>
    (systemGrades || []).filter(g => g.isActive).sort((a, b) => a.gradeNumber - b.gradeNumber),
    [systemGrades]
  );

  const uniqueSubjects = useMemo(() => {
    const subs = systemSubjects || [];
    const seen = new Map<string, CurriculumSubject>();
    for (const s of subs) {
      if (s.isActive && !seen.has(s.name.toLowerCase())) {
        seen.set(s.name.toLowerCase(), s);
      }
    }
    return Array.from(seen.values());
  }, [systemSubjects]);

  const getSubjectsForGrade = (gradeNum: string) => {
    if (!gradeNum || !systemGrades || !systemSubjects) return uniqueSubjects;
    const grade = systemGrades.find(g => g.gradeNumber.toString() === gradeNum);
    if (!grade) return uniqueSubjects;
    return (systemSubjects || []).filter(s => s.gradeId === grade.id && s.isActive);
  };

  const getTopicsForGradeAndSubject = (gradeNum: string, subjectName: string) => {
    if (!systemTopics || !systemSubjects || !systemGrades) return [];
    let filtered = [...(systemTopics || [])];

    if (gradeNum) {
      const grade = systemGrades.find(g => g.gradeNumber.toString() === gradeNum);
      if (grade) {
        const gradeSubjectIds = new Set(
          (systemSubjects || []).filter(s => s.gradeId === grade.id).map(s => s.id)
        );
        filtered = filtered.filter(t => gradeSubjectIds.has(t.subjectId));
      }
    }

    if (subjectName) {
      const matchingSubjectIds = new Set(
        (systemSubjects || []).filter(s => s.name.toLowerCase() === subjectName.toLowerCase()).map(s => s.id)
      );
      filtered = filtered.filter(t => matchingSubjectIds.has(t.subjectId));
    }

    const seen = new Map<string, CurriculumTopic>();
    for (const t of filtered) {
      if (!seen.has(t.name.toLowerCase())) {
        seen.set(t.name.toLowerCase(), t);
      }
    }
    return Array.from(seen.values());
  };

  const queryParams = new URLSearchParams();
  queryParams.set("page", page.toString());
  if (gradeFilter) queryParams.set("grade", gradeFilter);
  if (subjectFilter) queryParams.set("subject", subjectFilter);
  if (topicFilter) queryParams.set("topic", topicFilter);
  if (typeFilter) queryParams.set("questionType", typeFilter);
  if (searchQuery) queryParams.set("search", searchQuery);
  if (sortBy) queryParams.set("sortBy", sortBy);
  if (sortOrder) queryParams.set("sortOrder", sortOrder);

  const { data, isLoading } = useQuery<QuestionsResponse>({
    queryKey: ["/api/admin/quizzative/questions", page, gradeFilter, subjectFilter, topicFilter, typeFilter, searchQuery, sortBy, sortOrder],
    queryFn: async () => {
      const res = await fetch(`/api/admin/quizzative/questions?${queryParams.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/admin/quizzative/questions/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/quizzative/questions"] });
      setDeleteConfirmId(null);
      toast({ title: "Question removed" });
    },
    onError: (error: Error) => {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
    },
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: async (data: Record<string, any>) => {
      const res = await apiRequest("PATCH", "/api/admin/quizzative/questions/bulk", {
        ids: Array.from(selectedIds),
        ...data,
      });
      return res.json();
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/quizzative/questions"] });
      setSelectedIds(new Set());
      setBulkEditOpen(false);
      toast({ title: `${result.updated} questions updated` });
    },
    onError: (error: Error) => {
      toast({ title: "Bulk update failed", description: error.message, variant: "destructive" });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", "/api/admin/quizzative/questions/bulk", {
        ids: Array.from(selectedIds),
      });
      return res.json();
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/quizzative/questions"] });
      setSelectedIds(new Set());
      setBulkDeleteConfirm(false);
      toast({ title: `${result.deleted} questions removed` });
    },
    onError: (error: Error) => {
      toast({ title: "Bulk delete failed", description: error.message, variant: "destructive" });
    },
  });

  const FEEDBACK_STORAGE_KEY = "quizzative_question_feedback";

  const loadStoredFeedback = (): Record<string, "up" | "down"> => {
    try {
      const stored = localStorage.getItem(FEEDBACK_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch { return {}; }
  };

  const saveStoredFeedback = (feedback: Record<string, "up" | "down">) => {
    try {
      // Keep only last 500 entries to avoid localStorage bloat
      const entries = Object.entries(feedback);
      const trimmed = entries.length > 500 ? Object.fromEntries(entries.slice(-500)) : feedback;
      localStorage.setItem(FEEDBACK_STORAGE_KEY, JSON.stringify(trimmed));
    } catch {}
  };

  const [feedbackSent, setFeedbackSent] = useState<Record<string, "up" | "down">>(loadStoredFeedback);

  const sendFeedback = async (question: QuizzativeQuestion, rating: "up" | "down", feedbackText?: string) => {
    try {
      await apiRequest("POST", "/api/admin/quizzative/question-feedback", {
        question_bank_item_id: question.quizzativeId || null,
        rating,
        text: feedbackText || null,
        question_type: getQuestionTypeNumber(question.questionType),
        grade_level: question.grade ? `Grade ${question.grade}` : null,
        subject: question.subject || null,
        topic: question.topic || null,
        content_summary: (question.questionText || "").substring(0, 200),
      });
      const updated = { ...feedbackSent, [question.id]: rating };
      setFeedbackSent(updated);
      saveStoredFeedback(updated);
      toast({ title: rating === "up" ? "Thumbs up sent" : "Thumbs down sent" });
    } catch (error) {
      toast({ title: "Feedback failed", description: error instanceof Error ? error.message : "Unknown error", variant: "destructive" });
    }
  };

  function getQuestionTypeNumber(type: string): number {
    const map: Record<string, number> = {
      "multiple-choice": 1, "single-choice": 1, "short-answer": 2,
      "multi-answer": 3, "table-grid": 4, "long-answer": 5,
      "ordering": 6, "matching": 7, "categorising": 8,
    };
    return map[type.toLowerCase()] || 1;
  }

  const questions = data?.data || [];
  const meta = data?.meta;

  const allSelected = questions.length > 0 && questions.every(q => selectedIds.has(q.id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(questions.map(q => q.id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSearch = () => {
    setSearchQuery(searchInput);
    setPage(1);
  };

  const clearFilters = () => {
    setGradeFilter("");
    setSubjectFilter("");
    setTopicFilter("");
    setSearchQuery("");
    setSearchInput("");
    setPage(1);
  };

  const hasFilters = gradeFilter || subjectFilter || topicFilter || searchQuery;

  const getSubjectLabel = (s: string) => {
    const found = (data?.filters?.subjects || []).find(x => x.value === s.toLowerCase());
    if (found) return found.label;
    const fromSystem = uniqueSubjects.find(x => x.name.toLowerCase() === s.toLowerCase());
    return fromSystem?.displayName || s;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Quizzative Questions</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Review placement and manage questions synced from Quizzative. {meta ? `${meta.total} total` : ""}
          </p>
        </div>
      </div>

      <Tabs defaultValue="all-questions">
        <TabsList>
          <TabsTrigger value="all-questions">All Questions</TabsTrigger>
          <TabsTrigger value="flagged" className="gap-1">
            <Flag className="h-3.5 w-3.5" />
            Flagged Questions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all-questions" className="space-y-4 mt-4">
        <div className="flex items-center justify-end">
        <AlertDialog open={showPurgeConfirm} onOpenChange={setShowPurgeConfirm}>
          <Button variant="destructive" size="sm" onClick={() => setShowPurgeConfirm(true)} disabled={purgeMutation.isPending}>
            {purgeMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            <span className="ml-1">Purge All & Resync</span>
          </Button>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Purge All Quizzative Questions?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete all questions synced from Quizzative and clear the sync history. Locally created questions will not be affected. You can then run a fresh sync.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => purgeMutation.mutate()} className="bg-destructive text-destructive-foreground">
                Yes, Purge All
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="w-36">
              <Select value={gradeFilter || "all"} onValueChange={(v) => { setGradeFilter(v === "all" ? "" : v); setTopicFilter(""); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder="All Grades" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Grades</SelectItem>
                  {(data?.filters?.grades || []).map(g => (
                    <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Select value={subjectFilter || "all"} onValueChange={(v) => { setSubjectFilter(v === "all" ? "" : v); setTopicFilter(""); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder="All Subjects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subjects</SelectItem>
                  {(data?.filters?.subjects || []).map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-44">
              <Select value={topicFilter || "all"} onValueChange={(v) => { setTopicFilter(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder="All Topics" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Topics</SelectItem>
                  {(data?.filters?.topics || []).map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Select value={typeFilter || "all"} onValueChange={(v) => { setTypeFilter(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="multiple-choice">Multiple Choice</SelectItem>
                  <SelectItem value="multi-select">Multi Select</SelectItem>
                  <SelectItem value="matching">Matching</SelectItem>
                  <SelectItem value="order-match">Ordering</SelectItem>
                  <SelectItem value="fill-blank">Fill Blank</SelectItem>
                  <SelectItem value="true-false">True/False</SelectItem>
                  <SelectItem value="table-grid">Table Grid</SelectItem>
                  <SelectItem value="short-answer">Short Answer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Select value={`${sortBy}:${sortOrder}`} onValueChange={(v) => {
                const [field, order] = v.split(":");
                setSortBy(field);
                setSortOrder(order);
                setPage(1);
              }}>
                <SelectTrigger>
                  <ArrowUpDown className="h-4 w-4 mr-1.5 shrink-0" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="createdAt:desc">Newest First</SelectItem>
                  <SelectItem value="createdAt:asc">Oldest First</SelectItem>
                  <SelectItem value="updatedAt:desc">Recently Updated</SelectItem>
                  <SelectItem value="grade:asc">Grade</SelectItem>
                  <SelectItem value="subject:asc">Subject</SelectItem>
                  <SelectItem value="topic:asc">Topic</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 flex-1 min-w-[200px]">
              <Input
                placeholder="Search question text..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
              <Button variant="outline" size="icon" onClick={handleSearch}>
                <Search className="h-4 w-4" />
              </Button>
            </div>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {selectedIds.size > 0 && (
        <Card>
          <CardContent className="py-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <span className="text-sm font-medium">{selectedIds.size} question{selectedIds.size > 1 ? "s" : ""} selected</span>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => {
                  setBulkGrade("");
                  setBulkSubject("");
                  setBulkTopic("");
                  setBulkEditOpen(true);
                }}>
                  <Pencil className="h-3.5 w-3.5 mr-1" />
                  Edit Placement
                </Button>
                <Button variant="destructive" size="sm" onClick={() => setBulkDeleteConfirm(true)}>
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  Remove Selected
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading questions...</span>
            </div>
          ) : questions.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              {hasFilters ? "No questions match your filters." : "No Quizzative questions synced yet."}
            </div>
          ) : (
            <div className="divide-y">
              {/* Desktop header */}
              <div className="hidden lg:flex items-center gap-3 px-4 py-2.5 bg-muted/30 text-xs font-medium text-muted-foreground">
                <div className="w-8">
                  <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} />
                </div>
                <div className="flex-1 min-w-0">Question</div>
                <div className="w-72 shrink-0">Placement (Grade / Subject / Topic)</div>
                <div className="w-20 shrink-0 text-center">Type</div>
                <div className="w-16 shrink-0 text-center">Status</div>
                <div className="w-24 shrink-0 text-center">Created</div>
                <div className="w-20 shrink-0 text-right">Actions</div>
              </div>
              {questions.map((q) => (
                <div key={q.id} className="px-4 py-3">
                  {/* Mobile layout */}
                  <div className="lg:hidden space-y-2">
                    <button
                      className="text-sm text-left cursor-pointer hover:underline leading-snug w-full"
                      onClick={() => setPreviewQuestion(q)}
                    >
                      <span className="line-clamp-2">
                        <LatexText text={q.questionText || "(empty)"} />
                      </span>
                    </button>
                    <div className="flex flex-wrap gap-1.5 items-center">
                      <Badge variant="secondary" className="text-xs">G{q.grade}</Badge>
                      <Badge variant="secondary" className="text-xs capitalize">{getSubjectLabel(q.subject)}</Badge>
                      <Badge variant="outline" className="text-xs">{q.questionType}</Badge>
                      <Badge className={`text-xs ${q.status === "published" ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"}`}>{q.status}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">{new Date(q.createdAt).toLocaleDateString("en-JM", { timeZone: "America/Jamaica", month: "short", day: "numeric", year: "numeric" })}</span>
                      <div className="flex gap-0.5">
                        <Button variant="ghost" size="icon" onClick={() => { setViewingQuestion(q); startEditing(q); }} title="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteConfirmId(q.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                      </div>
                    </div>
                  </div>
                  {/* Desktop layout */}
                  <div className="hidden lg:flex items-start gap-3">
                  <div className="w-8 pt-0.5">
                    <Checkbox
                      checked={selectedIds.has(q.id)}
                      onCheckedChange={() => toggleSelect(q.id)}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <button
                      className="text-sm text-left cursor-pointer hover:underline leading-snug"
                      onClick={() => setPreviewQuestion(q)}
                    >
                      {q.questionImageUrl && (
                        <ImageIcon className="h-3.5 w-3.5 inline mr-1 text-muted-foreground" />
                      )}
                      <span className="line-clamp-2">
                        <LatexText text={q.questionText || "(empty)"} />
                      </span>
                    </button>
                  </div>
                  <div className="w-72 shrink-0">
                    <InlinePlacementEditor
                      question={q}
                      grades={grades}
                      systemSubjects={systemSubjects || []}
                      systemTopics={systemTopics || []}
                      systemGrades={systemGrades || []}
                    />
                  </div>
                  <div className="w-20 shrink-0 text-center pt-0.5">
                    <Badge variant="outline" className="text-xs">{q.questionType}</Badge>
                  </div>
                  <div className="w-16 shrink-0 text-center pt-0.5">
                    <Badge
                      className={q.status === "published"
                        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                        : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                      }
                    >
                      {q.status}
                    </Badge>
                  </div>
                  <div className="w-24 shrink-0 text-center pt-0.5">
                    <span className="text-xs text-muted-foreground">
                      {new Date(q.createdAt).toLocaleDateString("en-JM", { timeZone: "America/Jamaica", month: "short", day: "numeric", year: "numeric" })}
                    </span>
                  </div>
                  <div className="w-32 shrink-0 text-right pt-0.5">
                    <div className="flex justify-end gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => sendFeedback(q, "up")}
                        title="Good question"
                        className={feedbackSent[q.id] === "up" ? "text-green-500" : ""}
                        disabled={!!feedbackSent[q.id]}
                      >
                        <ThumbsUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => sendFeedback(q, "down")}
                        title="Bad question"
                        className={feedbackSent[q.id] === "down" ? "text-red-500" : ""}
                        disabled={!!feedbackSent[q.id]}
                      >
                        <ThumbsDown className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => { setViewingQuestion(q); startEditing(q); }} title="Edit Question">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteConfirmId(q.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {meta && meta.lastPage > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <span className="text-sm text-muted-foreground">
                Page {meta.currentPage} of {meta.lastPage} ({meta.total} total)
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.currentPage <= 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.currentPage >= meta.lastPage}
                  onClick={() => setPage(p => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!viewingQuestion} onOpenChange={(open) => { if (!open) { setViewingQuestion(null); setEditingQuestion(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3 pr-6">
              <div>
                <DialogTitle>{editingQuestion ? "Edit Question" : "Question Details"}</DialogTitle>
                <DialogDescription>{editingQuestion ? "Edit and save changes" : "Full view of the question"}</DialogDescription>
              </div>
              <div className="flex gap-2 shrink-0">
                {viewingQuestion && !editingQuestion && (
                  <>
                    <Button size="sm" variant="outline" className="gap-1.5" onClick={() => { setPreviewQuestion(viewingQuestion); setViewingQuestion(null); }}>
                      <Eye className="w-3.5 h-3.5" /> Preview
                    </Button>
                    <Button size="sm" className="gap-1.5" onClick={() => startEditing(viewingQuestion)}>
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </Button>
                  </>
                )}
              </div>
            </div>
          </DialogHeader>
          {viewingQuestion && editingQuestion ? (
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-medium uppercase tracking-wide">Question Text</Label>
                <Textarea className="mt-1 min-h-[80px]" value={editForm.questionText} onChange={(e) => setEditForm(f => ({...f, questionText: e.target.value}))} />
              </div>
              <div>
                <Label className="text-xs font-medium uppercase tracking-wide">Answer Options (one per line)</Label>
                <Textarea className="mt-1 min-h-[100px] font-mono text-sm" value={editForm.options} onChange={(e) => setEditForm(f => ({...f, options: e.target.value}))} />
              </div>
              <div>
                <Label className="text-xs font-medium uppercase tracking-wide">Correct Answer (option ID: a, b, c, d)</Label>
                <Input className="mt-1" value={editForm.correctAnswer} onChange={(e) => setEditForm(f => ({...f, correctAnswer: e.target.value}))} />
              </div>
              <div>
                <Label className="text-xs font-medium uppercase tracking-wide">Explanation</Label>
                <Textarea className="mt-1 min-h-[60px]" value={editForm.explanation} onChange={(e) => setEditForm(f => ({...f, explanation: e.target.value}))} />
              </div>
              <div>
                <Label className="text-xs font-medium uppercase tracking-wide">Difficulty</Label>
                <Select value={editForm.difficulty} onValueChange={(v) => setEditForm(f => ({...f, difficulty: v}))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setEditingQuestion(null)}>Cancel</Button>
                <Button onClick={saveEdit} disabled={editQuestionMutation.isPending}>
                  {editQuestionMutation.isPending ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</> : "Save Changes"}
                </Button>
              </DialogFooter>
            </div>
          ) : viewingQuestion && (() => {
            let parsedOptions: Array<{id: string; text: string}> = [];
            try {
              const raw = typeof viewingQuestion.options === "string" ? JSON.parse(viewingQuestion.options) : viewingQuestion.options;
              if (typeof raw === "string") {
                parsedOptions = JSON.parse(raw);
              } else if (Array.isArray(raw)) {
                parsedOptions = raw;
              }
            } catch {}

            const correctIds = (viewingQuestion.correctAnswer || "").split(",").map(s => s.trim().toLowerCase());
            const original = getOriginalData(viewingQuestion);

            return (
              <div className="space-y-5">
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Current Placement</label>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    <Badge variant="secondary">Grade {viewingQuestion.grade}</Badge>
                    <Badge variant="secondary" className="capitalize">{getSubjectLabel(viewingQuestion.subject)}</Badge>
                    <Badge variant="secondary">{viewingQuestion.topic}</Badge>
                    <Badge variant="outline">{viewingQuestion.questionType}</Badge>
                    <Badge variant="outline" className="capitalize">{viewingQuestion.difficulty}</Badge>
                    <Badge className={viewingQuestion.status === "published"
                      ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                      : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                    }>
                      {viewingQuestion.status}
                    </Badge>
                  </div>
                  {original && (original.gradeLevel || original.originalSubject || original.originalStrand) && (
                    <div className="mt-2 text-xs text-muted-foreground border rounded-md p-2 bg-muted/30">
                      <span className="font-medium">Original from Quizzative:</span>{" "}
                      {[
                        original.gradeLevel && `Grade: ${original.gradeLevel}`,
                        original.originalSubject && `Subject: ${original.originalSubject}`,
                        (original.originalStrand || original.originalTopic) && `Strand: ${original.originalStrand || original.originalTopic}`,
                      ].filter(Boolean).join(" | ")}
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Question</label>
                  <div className="mt-1 p-3 bg-muted/50 rounded-md text-sm whitespace-pre-wrap">
                    <LatexText text={viewingQuestion.questionText || "(empty)"} />
                  </div>
                </div>

                {viewingQuestion.questionImageUrl && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Image</label>
                    <div className="mt-1">
                      <img src={viewingQuestion.questionImageUrl} alt="Question" className="max-w-full max-h-64 rounded-md border" />
                    </div>
                  </div>
                )}

                {parsedOptions.length > 0 && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Answer Choices</label>
                    <div className="mt-1 space-y-2">
                      {parsedOptions.map((opt, idx) => {
                        const isCorrect = correctIds.includes(opt.id.toLowerCase());
                        const letter = String.fromCharCode(65 + idx);
                        return (
                          <div key={opt.id} className={`flex items-start gap-2 p-2.5 rounded-md border text-sm ${isCorrect ? "border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-950" : "border-border bg-background"}`}>
                            {isCorrect ? <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 text-muted-foreground/40 mt-0.5 shrink-0" />}
                            <span className="font-medium mr-1">{letter}.</span>
                            <span className={isCorrect ? "font-medium" : ""}><LatexText text={opt.text} /></span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {parsedOptions.length === 0 && viewingQuestion.correctAnswer && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Correct Answer</label>
                    <div className="mt-1 p-3 bg-green-50 dark:bg-green-950 rounded-md border border-green-300 dark:border-green-700 text-sm font-medium">
                      <LatexText text={viewingQuestion.correctAnswer} />
                    </div>
                  </div>
                )}

                {viewingQuestion.explanation && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Explanation</label>
                    <div className="mt-1 p-3 bg-muted/50 rounded-md text-sm whitespace-pre-wrap">
                      <LatexText text={viewingQuestion.explanation} />
                    </div>
                  </div>
                )}

                <div className="border-t pt-4">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Rate This Question</label>
                  <div className="mt-2 flex items-center gap-2">
                    {feedbackSent[viewingQuestion.id] ? (
                      <Badge variant={feedbackSent[viewingQuestion.id] === "up" ? "default" : "destructive"}>
                        {feedbackSent[viewingQuestion.id] === "up" ? "Rated good" : "Rated poor"}
                      </Badge>
                    ) : (
                      <>
                        <Button variant="outline" size="sm" onClick={() => sendFeedback(viewingQuestion, "up")} className="gap-1"><ThumbsUp className="h-4 w-4" /> Good</Button>
                        <Button variant="outline" size="sm" onClick={() => sendFeedback(viewingQuestion, "down")} className="gap-1"><ThumbsDown className="h-4 w-4" /> Poor</Button>
                      </>
                    )}
                    <span className="text-xs text-muted-foreground">Feedback improves future AI generation</span>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex items-center gap-2 mb-1">
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Quizzative ID</label>
                  </div>
                  <div className="text-xs font-mono bg-muted/50 rounded-md px-3 py-2 select-all">
                    {viewingQuestion.quizzativeId}
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <History className="h-3.5 w-3.5 text-muted-foreground" />
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Change History</label>
                  </div>
                  <QuestionChangeHistory questionId={viewingQuestion.id} />
                </div>
              </div>
            );
          })()}
          {!editingQuestion && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewingQuestion(null)}>Close</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={bulkEditOpen} onOpenChange={setBulkEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Placement for {selectedIds.size} Questions</DialogTitle>
            <DialogDescription>
              Only fill in the fields you want to change. Leave blank to keep the existing value.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Grade</label>
              <Select value={bulkGrade} onValueChange={(v) => { setBulkGrade(v); setBulkTopic(""); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Keep existing" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep">Keep existing</SelectItem>
                  {grades.map(g => (
                    <SelectItem key={g.gradeNumber} value={g.gradeNumber.toString()}>{g.displayName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Subject</label>
              <Select value={bulkSubject} onValueChange={(v) => { setBulkSubject(v); setBulkTopic(""); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Keep existing" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep">Keep existing</SelectItem>
                  {getSubjectsForGrade(bulkGrade === "keep" ? "" : bulkGrade).map(s => (
                    <SelectItem key={s.id} value={s.name.toLowerCase()}>{s.displayName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Topic</label>
              <Select value={bulkTopic || "keep"} onValueChange={(v) => setBulkTopic(v === "keep" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Keep existing" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep">Keep existing</SelectItem>
                  {(() => {
                    const systemOpts = getTopicsForGradeAndSubject(
                      bulkGrade === "keep" ? "" : bulkGrade,
                      bulkSubject === "keep" ? "" : bulkSubject
                    );
                    const fromData = (data?.filters?.topics || []);
                    const allNames = new Set<string>();
                    const items: Array<{key: string; value: string; label: string}> = [];
                    for (const t of systemOpts) {
                      if (!allNames.has(t.name.toLowerCase())) {
                        allNames.add(t.name.toLowerCase());
                        items.push({ key: t.id, value: t.name, label: t.displayName });
                      }
                    }
                    for (const t of fromData) {
                      if (!allNames.has(t.value.toLowerCase())) {
                        allNames.add(t.value.toLowerCase());
                        items.push({ key: `data-${t.value}`, value: t.value, label: t.label });
                      }
                    }
                    return items.map(i => (
                      <SelectItem key={i.key} value={i.value}>{i.label}</SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkEditOpen(false)}>Cancel</Button>
            <Button
              disabled={bulkUpdateMutation.isPending}
              onClick={() => {
                const updateData: Record<string, any> = {};
                if (bulkGrade && bulkGrade !== "keep") updateData.grade = parseInt(bulkGrade);
                if (bulkSubject && bulkSubject !== "keep") updateData.subject = bulkSubject;
                if (bulkTopic) updateData.topic = bulkTopic;
                if (Object.keys(updateData).length === 0) {
                  toast({ title: "Nothing to update", description: "Please change at least one field." });
                  return;
                }
                bulkUpdateMutation.mutate(updateData);
              }}
            >
              {bulkUpdateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Update {selectedIds.size} Questions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this question?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this Quizzative question from the system. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground"
              disabled={deleteMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (deleteConfirmId) deleteMutation.mutate(deleteConfirmId);
              }}
            >
              {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteConfirm} onOpenChange={setBulkDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {selectedIds.size} questions?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the selected Quizzative questions. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground"
              disabled={bulkDeleteMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                bulkDeleteMutation.mutate();
              }}
            >
              {bulkDeleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Remove {selectedIds.size} Questions
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <QuestionPreviewDialog
        question={previewQuestion}
        open={!!previewQuestion}
        onClose={() => setPreviewQuestion(null)}
        onEdit={previewQuestion ? () => { const q = previewQuestion; setPreviewQuestion(null); setViewingQuestion(q); startEditing(q); } : undefined}
      />

        </TabsContent>

        <TabsContent value="flagged" className="mt-4">
          <FlaggedQuestions />
        </TabsContent>
      </Tabs>
    </div>
  );
}
